import { useEffect, useState } from "react";
import { getBillInfo, getCustomerInvoice, getInvoiceMonths } from "../../api/cash/cash.api";
import SortableTableHeader from "../table/SortableTableHeader";
import InputGroup from "../form/InputGroup";
import {Panel} from "omoplay";
import CustomerInvoice from "./CustomerInvoice";
import { monthOptionLabel, monthRange, monthText, periodText, pickMonth } from "../../libs/invoiceMonths";

export const BillWrap = () => {
  /**
   * 이용월 풀다운은 **서버가 내려준 목록으로만** 채운다(2026-10-15).
   *
   * 종전에는 연도(2024~올해)·월(1~12)을 따로 골라 어떤 달이든 요청할 수 있었다. 그런데 legacy `/cash/bill` 은
   * 가입일과 무관하게 `monthly_price × store_count` 를 무조건 찍으므로, 9월 가입 고객이 7월 이하로 내려가면
   * 월 비용 33,000원짜리 **유령 내역서**가 보였다. 지금 10월인데 11·12월이, 무관한 2024년이 골라지는 것도 같은 구조.
   *
   * 어느 달이 의미 있는지(가입월·과금 기준일·구독·정산서)는 서버만 안다 → `GET /cash/invoice/months`.
   * 목록이 비면 "표시할 내역이 없다". `months === null` 은 아직 목록을 못 받은 상태(로딩)다.
   */
  const [months, setMonths] = useState(null);
  const [month, setMonth] = useState(null);
  const [bill, setBill] = useState([]);
  const [invoice, setInvoice] = useState(null);

  useEffect(() => {
    let canceled = false;
    getInvoiceMonths().then(({ data }) => {
      if (canceled) return;
      const list = Array.isArray(data?.months) ? data.months : [];
      setMonths(list);
      setMonth((prev) => pickMonth(list, prev));
    });
    return () => { canceled = true; };
  }, []);

  /**
   * A2 Phase G — 월에 따라 청구 근거가 완전히 갈린다.
   *  - cutoff(=서버 billing.net-cutoff-month, 2026-07) 미만: 기존 실시간 집계(/cash/bill)
   *  - cutoff 이상: sysadmin 이 확정한 고지서만(/cash/invoice)
   * 어느 쪽인지는 **서버가 내려주는 mode** 로만 판단한다(프론트에 cutoff 를 복제해 두면 양쪽이 어긋난다).
   * legacy 엔드포인트는 cutoff 이상 월에서 400 을 반환하므로 mode 확인 전에는 호출하지 않는다.
   */
  useEffect(() => {
    let canceled = false;

    setInvoice(null);
    setBill([]);
    if (!month) return undefined;

    getCustomerInvoice(month).then(({ data }) => {
      if (canceled) return;
      if (data?.mode !== "LEGACY") {
        setInvoice(data);
        return;
      }
      const range = monthRange(month);
      getBillInfo(range.startDate, range.endDate).then(({ data: legacy }) => {
        if (!canceled) setBill(legacy);
      });
    });

    return () => { canceled = true; };
  }, [month]);

  const headers = [
    { key: "createdAt", label: "사용 월", sortable: false },
    { key: "name", label: "항목", sortable: false },
    { key: "issuedAt", label: "사용 기간", sortable: false },
    {
      key: "category",
      label: "내용",
      sortable: false,
    },
    {
      key: "perPrice",
      label: "단가",
      sortable: false,
    },
    {
      key: "count",
      label: "사용량",
      className: "text-right",
      sortable: false,
    },
    {
      key: "price",
      label: "사용 금액",
      className: "text-right",
      sortable: false,
    }
  ];

  return (
    <div>
      <div className="p-4 w-full flex gap-4 items-center">
        <select
          className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-indigo-500 focus:border-indigo-500 block p-2 disabled:text-gray-400 hover:disabled:bg-gray-300"
          value={month ?? ""}
          disabled={!months || months.length === 0}
          onChange={(e) => setMonth(e.target.value || null)}
        >
          {(months ?? []).map((entry) => (
            <option key={entry.month} value={entry.month}>{monthOptionLabel(entry)}</option>
          ))}
        </select>
      </div>

      {months !== null && months.length === 0 && (
        <div className="mt-6 border rounded bg-gray-50 py-12 text-center">
          <div className="text-gray-700 font-semibold">표시할 이용 내역이 없습니다.</div>
          <div className="mt-2 text-sm text-gray-500">서비스 이용이 시작된 달이 끝나면 여기에 표시됩니다.</div>
        </div>
      )}

      {invoice ? (
        <CustomerInvoice
          invoice={invoice}
          variant="bill"
          periodLabel={periodText(month)}
        />
      ) : month && bill?.days ? (
      <>
      <div className="bg-white">
        <Panel items={[
          {title: `${monthText(month)} 합계 금액`, value: bill.totalPrice?.toLocaleString(), suffix: "원"},
          {title: "기간", value: periodText(month)}
        ]} />
      </div>
      <div className="w-full mt-8 flex-col gap-8">
        <InputGroup label="사용 내역">
          <div>
            <table className="w-full text-sm text-left rtl:text-right text-gray-500 border mb-4 relative ">
              <thead className="text-xs text-gray-700 uppercase rounded-tl border-b">
              <tr className="divide-x">
                {headers.map((header, index) => {
                  return (
                      <SortableTableHeader
                          key={"bill-wrap-table-header" + index}
                          header={header}
                      />
                  );
                })}
              </tr>
              </thead>
              <tbody>
              {bill.days
                  ?.map((row) => {
                    const cs = "px-5 py-2 ";
                    const rowspan = row.items.length;
                    const items = row.items
                    const firstItem = items.shift();

                    if (!firstItem) {
                      return <></>
                    }

                    return (
                        <>
                          <tr
                              className="bg-white border-b hover:bg-gray-50 divide-x"
                              key={"bill-" + row.day}
                          >
                            <td className={cs} rowSpan={rowspan}>
                              {row.day}
                            </td>
                            <td className={cs + "font-semibold text-gray-800"} rowSpan={rowspan}>
                              {row.type}
                            </td>
                            <td className={cs}>{firstItem.issuedAt}</td>
                            <td className={cs}>{firstItem.historyType}</td>
                            <td className={cs}>
                              ₩{firstItem.unitPrice.toLocaleString()}
                            </td>
                            <td className={cs + "text-right"}>
                              {firstItem.count.toLocaleString()}
                            </td>
                            <td className={cs + " text-right"}>
                              ₩{(firstItem.price).toLocaleString()}
                            </td>
                          </tr>
                          {items.map((item, j) => {
                            return (
                                <tr
                                    className="border-b bg-white hover:bg-gray-50 divide-x"
                                    key={"bill-category-" + j}
                                >
                                  <td className={cs + "border-l"}>{item.issuedAt}</td>
                                  <td className={cs}>
                                    {item.historyType}
                                  </td>
                                  <td className={cs}>
                                    ₩{item.unitPrice.toLocaleString()}
                                  </td>
                                  <td className={cs + "text-right"}>
                                    {item.count.toLocaleString()}
                                  </td>
                                  <td className={cs + " text-right"}>
                                    ₩{(item.price).toLocaleString()}
                                  </td>
                                </tr>
                            );
                          })}
                        </>
                    );
                  })}
              <tr className="bg-gray-50 border-b text-sm divide-x">
                <th className="px-5 py-3">총합</th>
                <th className="text-right px-5 font-bold" colSpan={6}>
                  ₩{bill?.totalPrice?.toLocaleString()}
                </th>
              </tr>
              </tbody>
            </table>
          </div>
        </InputGroup>
      </div>

      </>
      ) : null}
    </div>
  );
};

export default BillWrap;
